/**
 * Hook personalizado para gestionar entrevistas del Hiring Manager
 */

import { useState, useCallback } from 'react'
import { hiringManagerInterviewService } from '@/features/rh/api/hiringManagerInterviewService'
import type {
  HiringManagerInterview,
  HiringManagerInterviewRegistration,
  HiringManagerInterviewDecision
} from '../types/hiringManagerInterview'

export const useHiringManagerInterviews = () => {
  const [interviews, setInterviews] = useState<HiringManagerInterview[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Carga todas las entrevistas (con filtro opcional por área)
   */
  const loadInterviews = useCallback(async (areaId?: number) => {
    setIsLoading(true)
    setError(null)

    try {
      const data = await hiringManagerInterviewService.getAllInterviews(areaId)
      setInterviews(data)
      return data
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error loading hiring manager interviews:', err)
      setError(err.message || 'Error al cargar las entrevistas')
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Crea una nueva entrevista
   */
  const createInterview = useCallback(
    async (data: HiringManagerInterviewRegistration) => {
      setIsLoading(true)
      setError(null)

      try {
        const newInterview = await hiringManagerInterviewService.createInterview(data)
        setInterviews(prev => [...prev, newInterview])
        return newInterview
      } catch (errCrudo: unknown) {
        const err = errCrudo as {
          message?: string;
          statusCode?: number;
          status?: number;
          error?: string;
          response?: { status?: number; data?: { message?: string } };
        };
        console.error('Error creating interview:', err)
        setError(err.message || 'Error al crear la entrevista')
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  /**
   * Registra la decisión del hiring manager
   */
  const recordDecision = useCallback(
    async (id: number, decision: HiringManagerInterviewDecision) => {
      setIsLoading(true)
      setError(null)

      try {
        const updated = await hiringManagerInterviewService.recordDecision(id, decision)
        setInterviews(prev =>
          prev.map(interview => interview.id === id ? updated : interview)
        )
        return updated
      } catch (errCrudo: unknown) {
        const err = errCrudo as {
          message?: string;
          statusCode?: number;
          status?: number;
          error?: string;
          response?: { status?: number; data?: { message?: string } };
        };
        console.error('Error recording decision:', err)
        setError(err.message || 'Error al registrar la decisión')
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  /**
   * Elimina una entrevista
   */
  const deleteInterview = useCallback(async (id: number) => {
    setIsLoading(true)
    setError(null)

    try {
      await hiringManagerInterviewService.deleteInterview(id)
      setInterviews(prev => prev.filter(interview => interview.id !== id))
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error deleting interview:', err)
      setError(err.message || 'Error al eliminar la entrevista')
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Sube el archivo de resultados de la entrevista
   */
  const uploadResultsFile = useCallback(async (interviewId: number, file: File) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await hiringManagerInterviewService.uploadResultsFile(interviewId, file)
      
      // Recargar la entrevista para obtener la URL del archivo
      const updated = await hiringManagerInterviewService.getInterviewById(interviewId)
      setInterviews(prev =>
        prev.map(interview => interview.id === interviewId ? updated : interview)
      )
      
      return response
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error uploading results file:', err)
      setError(err.message || 'Error al subir el archivo')
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Recarga una entrevista específica
   */
  const reloadInterview = useCallback(async (id: number) => {
    try {
      const updated = await hiringManagerInterviewService.getInterviewById(id)
      setInterviews(prev =>
        prev.map(interview => interview.id === id ? updated : interview)
      )
      return updated
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error reloading interview:', err)
      throw err
    }
  }, [])

  /**
   * Obtiene entrevistas pendientes de decisión
   */
  const getPendingDecisions = useCallback(() => {
    return hiringManagerInterviewService.getPendingDecisions(interviews)
  }, [interviews])

  /**
   * Obtiene entrevistas completadas
   */
  const getCompletedInterviews = useCallback(() => {
    return hiringManagerInterviewService.getCompletedInterviews(interviews)
  }, [interviews])

  return {
    interviews,
    isLoading,
    error,
    loadInterviews,
    createInterview,
    recordDecision,
    deleteInterview,
    uploadResultsFile,
    reloadInterview,
    getPendingDecisions,
    getCompletedInterviews,
  }
}
